<template>
  <div class="got-body">
    <div class="got-stats">
      <div class="got-stat got-stat--blue">
        <div class="got-stat__info">
          <p class="got-stat__label">Stores</p>
          <p class="got-stat__value">{{ storesCount }}</p>
        </div>
        <vue-feather type="shopping-bag" size="22" class="got-stat__icon" />
      </div>
      <div class="got-stat got-stat--green">
        <div class="got-stat__info">
          <p class="got-stat__label">Members</p>
          <p class="got-stat__value">{{ membersCount }}</p>
        </div>
        <vue-feather type="users" size="22" class="got-stat__icon" />
      </div>
      <div class="got-stat got-stat--purple">
        <div class="got-stat__info">
          <p class="got-stat__label">Active Codes</p>
          <p class="got-stat__value">{{ activeCodesCount }}</p>
        </div>
        <vue-feather type="key" size="22" class="got-stat__icon" />
      </div>
    </div>

    <div class="got-card">
      <p class="got-card__title">Group Information</p>
      <div class="got-grid">
        <div class="got-field">
          <span class="got-field__label">Group ID</span>
          <span class="got-field__value">{{ group.id }}</span>
        </div>
        <div class="got-field">
          <span class="got-field__label">Owner</span>
          <span class="got-field__value">User #{{ group.owner_id }}</span>
        </div>
        <div class="got-field">
          <span class="got-field__label">Created</span>
          <span class="got-field__value">{{ formatDate(group.created_at) }}</span>
        </div>
        <div class="got-field">
          <span class="got-field__label">Your Role</span>
          <span class="got-field__value">{{ displayRole }}</span>
        </div>
      </div>
    </div>

    <div class="got-card">
      <p class="got-card__title">Active Subscription</p>
      <div v-if="loadingSubscription" class="got-loading">
        <span class="got-spinner" />Loading subscription…
      </div>
      <hs-empty-state
        v-else-if="!subscription"
        icon="award"
        title="No active subscription"
        description="This group has no active subscription plan."
      />
      <div v-else class="got-sub">
        <div class="got-sub__header">
          <div>
            <p class="got-sub__name">{{ subscription.plan?.display_name || "Unknown Plan" }}</p>
            <p class="got-sub__price">
              {{ formatPrice(subscription.plan?.price_amount, subscription.plan?.currency) }}
              <span v-if="subscription.plan?.billing_period_days">
                / {{ subscription.plan.billing_period_days }} days
              </span>
            </p>
          </div>
          <hs-badge :tone="subscription.has_active_subscription ? 'success' : 'danger'">
            {{ subscription.has_active_subscription ? "Active" : "Expired" }}
          </hs-badge>
        </div>
        <p v-if="subscription.expires_at" class="got-sub__expiry">
          <vue-feather type="calendar" size="12" />
          Expires: {{ formatDate(subscription.expires_at) }}
        </p>
        <div v-if="subscription.features && Object.keys(subscription.features).length">
          <p class="got-section-label">Included Features</p>
          <div class="got-badges">
            <hs-badge v-for="(value, key) in subscription.features" :key="key" tone="primary">
              {{ formatFeatureName(key) }}: {{ formatFeatureValue(value) }}
            </hs-badge>
          </div>
        </div>
      </div>
    </div>

    <div class="got-card">
      <p class="got-card__title">Your Permissions</p>
      <div v-if="userPermissions.length" class="got-badges">
        <hs-badge v-for="perm in userPermissions" :key="perm" tone="success">
          {{ formatPermission(perm) }}
        </hs-badge>
      </div>
      <p v-else class="got-muted">No specific permissions assigned (read-only access)</p>
    </div>

    <div class="got-card">
      <p class="got-card__title">Group Settings</p>
      <div class="got-badges">
        <hs-badge v-if="group.settings?.sync_products" tone="primary">Product Sync</hs-badge>
        <hs-badge v-if="group.settings?.sync_services" tone="warning">Service Sync</hs-badge>
        <hs-badge v-if="group.settings?.require_sku" tone="neutral">SKU Required</hs-badge>
        <p
          v-if="!group.settings?.sync_products && !group.settings?.sync_services && !group.settings?.require_sku"
          class="got-muted"
        >No group settings configured</p>
      </div>
    </div>

    <div class="got-actions">
      <hs-button v-if="canManageGroup" variant="secondary" icon="key" @click="showGenerateCodeModal = true">
        Generate Join Code
      </hs-button>
      <hs-button
        :variant="isDefaultGroup ? 'secondary' : 'ghost'"
        :icon="isDefaultGroup ? 'check-circle' : 'star'"
        :loading="settingDefault"
        @click="handleSetAsDefault"
      >
        {{ isDefaultGroup ? "Default Group" : "Set as Default" }}
      </hs-button>
      <hs-button v-if="canLeave" variant="ghost" icon="log-out" tone="danger" @click="$emit('leave-group')">
        Leave Group
      </hs-button>
    </div>

    <generate-join-code-modal
      v-if="showGenerateCodeModal"
      :group="group"
      @close="showGenerateCodeModal = false"
      @generated="onCodeGenerated"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import GenerateJoinCodeModal from "@/components/settings/group/GenerateJoinCodeModal.vue";
import { HsButton, HsBadge, HsEmptyState } from "@/components/ui";

export default {
  name: "GroupOverviewTab",
  components: { GenerateJoinCodeModal, HsButton, HsBadge, HsEmptyState },
  props: {
    group: { type: Object, required: true },
    storeId: { type: Number, required: true },
  },
  emits: ["refresh", "leave-group"],
  setup(props, { emit }) {
    const store = useStore();
    const showGenerateCodeModal = ref(false);
    const stats = ref({ stores_count: 0, members_count: 0, active_codes_count: 0 });
    const loadingStats = ref(false);
    const subscription = ref(null);
    const loadingSubscription = ref(false);
    const settingDefault = ref(false);

    const canManageGroup = computed(() =>
      store.getters["groups/canManageGroup"] || store.getters["groups/isGroupOwner"]
    );
    const displayRole = computed(() =>
      store.getters["groups/currentUserRole"] || props.group.membership?.role || "MEMBER"
    );
    const canLeave = computed(() => !store.getters["groups/isGroupOwner"]);
    const isDefaultGroup = computed(() => {
      const currentStore = store.getters.getUserStores?.find((s) => s.id === props.storeId);
      return currentStore?.default_group_id === props.group.id;
    });
    const userPermissions = computed(() => store.getters["groups/currentGroupPermissions"] || []);
    const storesCount = computed(() => (loadingStats.value ? "—" : stats.value.stores_count));
    const membersCount = computed(() => (loadingStats.value ? "—" : stats.value.members_count));
    const activeCodesCount = computed(() => (loadingStats.value ? "—" : stats.value.active_codes_count));

    const loadStats = async () => {
      loadingStats.value = true;
      try {
        const response = await http.get(`/v2/groups/${props.group.id}/stats`);
        stats.value = response.data;
      } catch (err) {
        console.error("Failed to load stats", err);
      } finally {
        loadingStats.value = false;
      }
    };

    const loadSubscription = async () => {
      loadingSubscription.value = true;
      try {
        const response = await http.get(`/v1/groups/${props.group.id}/subscription`);
        subscription.value = response.data;
      } catch (err) {
        console.error("Failed to load subscription", err);
        subscription.value = null;
      } finally {
        loadingSubscription.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown";
      return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const formatPermission = (permission) =>
      permission.replace("CAN_", "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

    const formatPrice = (amount, currency) => {
      if (!amount || amount === 0) return "Free";
      const curr = currency || "RWF";
      try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: curr, maximumFractionDigits: 2 }).format(Number(amount));
      } catch {
        return `${amount} ${curr}`;
      }
    };

    const formatFeatureName = (key) =>
      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

    const formatFeatureValue = (value) => {
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (value === null || value === undefined) return "N/A";
      return value;
    };

    const onCodeGenerated = () => {
      emit("refresh");
      loadStats();
      showGenerateCodeModal.value = false;
    };

    const handleSetAsDefault = async () => {
      settingDefault.value = true;
      try {
        await http.post(`/v1/store/${props.storeId}/default-group`, { group_id: props.group.id });
        await store.dispatch("getAllUsersStore");
        toast.success(`${props.group.name} set as default group`);
        emit("refresh");
      } catch (err) {
        console.error("Failed to set default group", err);
        toast.error(err?.response?.data?.error?.message || "Failed to set default group");
      } finally {
        settingDefault.value = false;
      }
    };

    onMounted(() => {
      loadStats();
      loadSubscription();
    });

    return {
      showGenerateCodeModal, canManageGroup, canLeave, displayRole, userPermissions,
      subscription, loadingSubscription, storesCount, membersCount, activeCodesCount,
      formatDate, formatPermission, formatPrice, formatFeatureName, formatFeatureValue,
      onCodeGenerated, settingDefault, isDefaultGroup, handleSetAsDefault,
    };
  },
};
</script>

<style scoped>
.got-body {
  display: grid;
  gap: 1.1rem;
}

.got-stats {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 520px) {
  .got-stats { grid-template-columns: 1fr; }
}

.got-stat {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 1rem 1.1rem;
}

.got-stat--blue  { border-left: 3px solid #3b82f6; }
.got-stat--green { border-left: 3px solid #10b981; }
.got-stat--purple { border-left: 3px solid #8b5cf6; }
.got-stat--blue .got-stat__icon  { color: #3b82f6; }
.got-stat--green .got-stat__icon { color: #10b981; }
.got-stat--purple .got-stat__icon { color: #8b5cf6; }

.got-stat__label {
  color: var(--hs-muted);
  font-size: 0.78rem;
  font-weight: 600;
  margin: 0;
}

.got-stat__value {
  color: var(--hs-text);
  font-size: 1.6rem;
  font-weight: 950;
  line-height: 1.15;
  margin: 0;
}

.got-card {
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  display: grid;
  gap: 0.85rem;
  padding: 1.1rem 1.25rem;
}

.got-card__title {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 750;
  margin: 0;
}

.got-grid {
  display: grid;
  gap: 0.65rem;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 480px) {
  .got-grid { grid-template-columns: 1fr; }
}

.got-field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.got-field__label {
  color: var(--hs-muted);
  font-size: 0.7rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.got-field__value {
  color: var(--hs-text);
  font-size: 0.85rem;
  font-weight: 550;
}

.got-loading {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.875rem;
  gap: 0.65rem;
  padding: 1rem 0;
}

.got-spinner {
  animation: got-spin 0.8s linear infinite;
  border: 2px solid color-mix(in srgb, var(--hs-primary) 28%, transparent);
  border-radius: 50%;
  border-top-color: var(--hs-primary);
  display: inline-block;
  height: 1.1rem;
  width: 1.1rem;
}

@keyframes got-spin { to { transform: rotate(360deg); } }

.got-sub { display: grid; gap: 0.75rem; }

.got-sub__header {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.got-sub__name {
  color: var(--hs-text);
  font-size: 0.95rem;
  font-weight: 750;
  margin: 0;
}

.got-sub__price {
  color: var(--hs-muted);
  font-size: 0.82rem;
  margin: 0.15rem 0 0;
}

.got-sub__expiry {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.82rem;
  gap: 0.35rem;
  margin: 0;
}

.got-section-label {
  color: var(--hs-muted);
  font-size: 0.7rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  margin: 0 0 0.4rem;
  text-transform: uppercase;
}

.got-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.got-muted {
  color: var(--hs-muted);
  font-size: 0.83rem;
  margin: 0;
}

.got-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
</style>
